import { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { MonthlyStats } from '../../types/property';
import { supabase } from '../../config/supabase';
import toast from 'react-hot-toast';
import { getMonthlyDataByYear, getAvailableYears } from '../../services/monthlyDataImport';

interface MonthlyDataEditorProps {
  propertyId: string;
  onDataUpdated: () => void;
}

export const MonthlyDataEditor = ({ propertyId, onDataUpdated }: MonthlyDataEditorProps) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [monthlyData, setMonthlyData] = useState<MonthlyStats[]>([]);
  const [editingData, setEditingData] = useState<Record<string, MonthlyStats>>({});
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [availableYears, setAvailableYears] = useState<number[]>([]);

  // Nombres de los meses para mostrar en la interfaz
  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // Cargar años disponibles al montar el componente
  useEffect(() => {
    const loadAvailableYears = async () => {
      try {
        const years = await getAvailableYears(propertyId);
        setAvailableYears(years);
        
        if (years.length > 0) {
          setSelectedYear(years[0]); // Seleccionar el año más reciente por defecto
        }
      } catch (error: any) {
        console.error('Error al cargar años disponibles:', error);
        toast.error('Error al cargar años disponibles');
      }
    };

    loadAvailableYears();
  }, [propertyId]);

  // Cargar datos mensuales cuando cambia el año seleccionado
  useEffect(() => {
    const loadMonthlyData = async () => {
      if (!selectedYear) return;
      
      setLoading(true);
      try {
        const data = await getMonthlyDataByYear(propertyId, selectedYear);
        setMonthlyData(data);
        
        // Inicializar el estado de edición con los datos actuales
        const initialEditState: Record<string, MonthlyStats> = {};
        data.forEach(item => {
          initialEditState[item.id] = { ...item };
        });
        setEditingData(initialEditState);
      } catch (error: any) {
        console.error('Error al cargar datos mensuales:', error);
        toast.error('Error al cargar datos mensuales');
      } finally {
        setLoading(false);
      }
    };

    loadMonthlyData();
  }, [propertyId, selectedYear]);

  // Manejar cambios en los campos editables
  const handleInputChange = (id: string, field: keyof MonthlyStats, value: any) => {
    setEditingData(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: typeof value === 'string' && !isNaN(Number(value)) ? Number(value) : value
      }
    }));
  };

  // Calcular el beneficio basado en los valores actuales
  const calculateProfit = (id: string) => {
    const data = editingData[id];
    if (!data) return;
    
    const revenue = data.revenue || 0;
    const cleaningCosts = data.cleaningCosts || 0;
    const inmohostCosts = data.inmohostCosts || 0;
    
    const profit = revenue - cleaningCosts - inmohostCosts;
    
    handleInputChange(id, 'profit', profit);
  };

  // Guardar los cambios de un registro específico
  const saveRecord = async (id: string) => {
    const data = editingData[id];
    if (!data) return;
    
    setSaving(true);
    try {
      const { error } = await supabase
        .from('monthly_stats')
        .update({
          revenue: data.revenue,
          cleaning_costs: data.cleaningCosts,
          inmohost_costs: data.inmohostCosts,
          profit: data.profit,
          occupancy_rate: data.occupancyRate,
          reservations: data.reservations,
          average_night_price: data.averageNightPrice,
          average_stay_duration: data.averageStayDuration
        })
        .eq('id', id);
      
      if (error) throw error;
      
      toast.success('Datos actualizados correctamente');
      
      // Actualizar la lista de datos
      setMonthlyData(prev => 
        prev.map(item => item.id === id ? { ...data } : item)
      );
      
      // Notificar al componente padre
      onDataUpdated();
    } catch (error: any) {
      console.error('Error al guardar datos:', error);
      toast.error(`Error al guardar: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Eliminar un registro
  const deleteRecord = async (id: string) => {
    if (!confirm('¿Estás seguro de que deseas eliminar este registro? Esta acción no se puede deshacer.')) {
      return;
    }
    
    setSaving(true);
    try {
      const { error } = await supabase
        .from('monthly_stats')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      toast.success('Registro eliminado correctamente');
      
      // Actualizar la lista de datos
      setMonthlyData(prev => prev.filter(item => item.id !== id));
      
      // Eliminar del estado de edición
      const newEditingData = { ...editingData };
      delete newEditingData[id];
      setEditingData(newEditingData);
      
      // Notificar al componente padre
      onDataUpdated();
    } catch (error: any) {
      console.error('Error al eliminar registro:', error);
      toast.error(`Error al eliminar: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <div className="p-4">
        <h2 className="text-xl font-semibold mb-4">Editar Datos Mensuales</h2>
        
        {/* Selector de año */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Filtrar por año:
          </label>
          <select
            value={selectedYear || ''}
            onChange={(e) => setSelectedYear(e.target.value ? Number(e.target.value) : null)}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
          >
            <option value="">Todos los años</option>
            {availableYears.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
        
        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Cargando datos...</p>
          </div>
        ) : monthlyData.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No hay datos disponibles para el año seleccionado
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mes
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Facturación (€)
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gastos Limpieza (€)
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gastos Inmohost (€)
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Beneficio (€)
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ocupación (%)
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reservas
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Precio Medio (€)
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estancia Media
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {monthlyData.map((data) => {
                  const editData = editingData[data.id];
                  if (!editData) return null;
                  
                  return (
                    <tr key={data.id}>
                      <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                        {monthNames[data.month - 1]} {data.year}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm">
                        <input
                          type="number"
                          value={editData.revenue || 0}
                          onChange={(e) => {
                            handleInputChange(data.id, 'revenue', e.target.value);
                            calculateProfit(data.id);
                          }}
                          className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm">
                        <input
                          type="number"
                          value={editData.cleaningCosts || 0}
                          onChange={(e) => {
                            handleInputChange(data.id, 'cleaningCosts', e.target.value);
                            calculateProfit(data.id);
                          }}
                          className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm">
                        <input
                          type="number"
                          value={editData.inmohostCosts || 0}
                          onChange={(e) => {
                            handleInputChange(data.id, 'inmohostCosts', e.target.value);
                            calculateProfit(data.id);
                          }}
                          className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm">
                        <input
                          type="number"
                          value={editData.profit || 0}
                          readOnly
                          className="w-20 px-2 py-1 text-sm bg-gray-50 border border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={editData.occupancyRate || 0}
                          onChange={(e) => handleInputChange(data.id, 'occupancyRate', e.target.value)}
                          className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm">
                        <input
                          type="number"
                          min="0"
                          value={editData.reservations || 0}
                          onChange={(e) => handleInputChange(data.id, 'reservations', e.target.value)}
                          className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm">
                        <input
                          type="number"
                          min="0"
                          value={editData.averageNightPrice || 0}
                          onChange={(e) => handleInputChange(data.id, 'averageNightPrice', e.target.value)}
                          className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm">
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={editData.averageStayDuration || 0}
                          onChange={(e) => handleInputChange(data.id, 'averageStayDuration', e.target.value)}
                          className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm">
                        <div className="flex space-x-2">
                          <Button
                            type="button"
                            onClick={() => saveRecord(data.id)}
                            loading={saving}
                            size="sm"
                            variant="primary"
                          >
                            Guardar
                          </Button>
                          <Button
                            type="button"
                            onClick={() => deleteRecord(data.id)}
                            loading={saving}
                            size="sm"
                            variant="outline"
                          >
                            Eliminar
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
};
